/**
 * Persistence CONTRACT for the native survey builder (VER-109).
 *
 * The builder never talks to the network directly. Instead it accepts three
 * injected async callbacks; the backend agent wires these to the real HTTP layer
 * (client/api/survey.ts). Until then the default `mockPersistence` below keeps an
 * in-memory store so the builder runs standalone (and in tests).
 *
 * See builder/INTEGRATION.md for the wire contract (endpoints, promoted columns,
 * the opaque `definition` blob shape).
 */
import type { SurveyDefinition } from "./survey.types";
import { MOCK_USER_SURVEYS } from "./builder.mock";

export type SurveyPersistence = {
    /** Load all editable (non-seed) surveys for the current organisation. */
    onLoadSurveys: () => Promise<SurveyDefinition[]>;
    /** Create or update a survey; returns the persisted definition (authoritative). */
    onSaveSurvey: (definition: SurveyDefinition) => Promise<SurveyDefinition>;
    /** Publish a survey by id; returns the published definition with a bumped version. */
    onPublishSurvey: (id: string) => Promise<SurveyDefinition>;
    /** Optional hard delete. */
    onDeleteSurvey?: (id: string) => Promise<void>;
};

const clone = (definition: SurveyDefinition): SurveyDefinition =>
    JSON.parse(JSON.stringify(definition)) as SurveyDefinition;

/**
 * Build an in-memory persistence implementation. Each store is independent, so
 * tests can spin up an isolated one. Mirrors the eventual server semantics:
 * save = upsert by id, publish = status "published" + version bump.
 */
export const createMockPersistence = (seed: SurveyDefinition[] = MOCK_USER_SURVEYS): SurveyPersistence => {
    const store = new Map<string, SurveyDefinition>();
    seed.forEach((s) => store.set(s.id, clone(s)));

    return {
        onLoadSurveys: async () => Array.from(store.values()).map(clone),
        onSaveSurvey: async (definition) => {
            const saved = clone(definition);
            store.set(saved.id, saved);
            return clone(saved);
        },
        onPublishSurvey: async (id) => {
            const existing = store.get(id);
            if (!existing) {
                throw new Error(`Cannot publish unknown survey "${id}"`);
            }
            const published: SurveyDefinition = {
                ...clone(existing),
                status: "published",
                version: Math.max(existing.version, 0) + 1,
            };
            store.set(id, published);
            return clone(published);
        },
        onDeleteSurvey: async (id) => {
            store.delete(id);
        },
    };
};

/** Default shared mock persistence used when no callbacks are injected. */
export const mockPersistence: SurveyPersistence = createMockPersistence();
